import { ArrowUpRightIcon } from './icons';
import { usePrice } from '../lib/currency';
import { useT } from '../i18n';
import { REDIRECT_MS } from '../../shared/out.mjs';
import './demo/demo.css';

/**
 * The interstitial shown after "Get tickets", for any marketplace: where you're going, a progress
 * bar that fills until the redirect, the event, the price the board showed and what happens next
 * on the partner's page, and a Continue button that works from the first paint. No countdown
 * theatre — the bar is the countdown. pages/Go.tsx (and, before the app loads, the /out/ shell's
 * inline script: shared/out.mjs) owns the timer and the navigation; with `auto` off (no timer: the
 * page was not opened from one of ours) the card only offers Continue.
 *
 * NO SUPERLATIVES (2026-10-19). It said "Best available price we found" for whichever partner was
 * clicked — false for two of three partners on a shared board, and TicketNetwork's affiliate terms
 * forbid "cheapest", "lowest" or any unverified superlative. The label names the partner and the
 * number is per ticket with its fee basis (`allIn`), exactly as the board showed it; a party of
 * several is told that seats together can cost more, never a computed total.
 *
 * TEXT BUDGET: the owner finds the site wordy, so the card grew by at most two lines at 390 px
 * ("About 2 seconds" went): what the partner's page will ask, and that you pay the partner directly.
 *
 * `shell`: the prerendered first frame, before the query is read. A class the inline script puts on
 * <html> picks its variant without a re-render: out-manual (no automatic redirect: "one click away",
 * no bar). `pending` holds the event and price block's place until they arrive, so Continue never
 * moves under a thumb.
 */
const RedirectCard = ({
  marketplace,
  event,
  price,
  allIn,
  suspect = false,
  qty,
  quantity,
  presetQty,
  pending = false,
  shell = false,
  continueHref,
  auto = true,
  autoMs = REDIRECT_MS,
}: {
  marketplace: string;
  event?: { name: string; when: string; venue: string };
  /** per ticket, USD */
  price?: number | null;
  allIn?: boolean;
  /** the board's "price not confirmed" caution */
  suspect?: boolean;
  /** the party size picked on the board */
  qty?: number | null;
  /** how the partner's page takes the party size (src/data/marketplaces.ts) */
  quantity?: 'preset' | 'asks';
  /** 'preset' partners: the quantity our link carries */
  presetQty?: number | null;
  pending?: boolean;
  shell?: boolean;
  continueHref: string;
  auto?: boolean;
  autoMs?: number;
}) => {
  const money = usePrice();
  const t = useT();
  const priced = typeof price === 'number' && price > 0;
  const party = typeof qty === 'number' && qty >= 1 && qty <= 20 ? qty : null;
  // What the partner's page does with the party size, so its quantity step is no surprise; for a
  // party, that the per-ticket price above can rise for seats together (never a computed total).
  // (Not in the shell: the party size is only read after mount.)
  const step = shell
    ? null
    : quantity === 'asks'
      ? party
        ? t('{marketplace} will ask how many tickets — choose {n}.', { marketplace, n: party })
        : t('{marketplace} will ask how many tickets you need.', { marketplace })
      : quantity === 'preset' && presetQty
        ? t('Opens with {n} ticket selected.|Opens with {n} tickets selected.', { n: presetQty })
        : null;
  const next = [step, party && party > 1 && priced ? t('Seats together can cost more each.') : null].filter(Boolean).join(' ');
  // The shell's variants: the inline script's <html> classes choose between them before first paint.
  const autoOnly = shell ? ' [.out-manual_&]:hidden' : '';

  return (
    <div className="w-full max-w-[440px] rounded-xl border border-border bg-card p-6 pb-5">
      <div role="status" aria-live="polite">
        <h1 className="text-[17px] font-medium tracking-tight">
          {shell ? (
            <>
              <span className="[.out-manual_&]:hidden">{t('Taking you to {marketplace}…', { marketplace })}</span>
              <span className="hidden [.out-manual_&]:inline">{t('{marketplace} is one click away', { marketplace })}</span>
            </>
          ) : auto ? (
            t('Taking you to {marketplace}…', { marketplace })
          ) : (
            t('{marketplace} is one click away', { marketplace })
          )}
        </h1>
      </div>
      {auto && (
        <div className={`mt-3 h-[5px] overflow-hidden rounded-full bg-muted${autoOnly}`} aria-hidden>
          <i className="redirect-bar" style={{ '--redirect-ms': `${autoMs}ms` } as React.CSSProperties} />
        </div>
      )}

      {/* Held at the common height (a party of 2+, a two-line venue at 390 px) from the first paint, so
          the event arriving never moves Continue; an unknown event lets the card shrink. */}
      <div className={pending || event ? 'min-h-[168px] sm:min-h-[148px]' : undefined}>
        {event && (
          <p className="mt-4 text-[15px] font-medium">
            {event.name}
            <span className="block text-[13px] font-normal text-muted-foreground">
              {[event.when, event.venue].filter(Boolean).join(' · ')}
            </span>
          </p>
        )}

        {priced && (
          <>
            <p className="mt-3 text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground">
              {t('Price we saw on {marketplace}', { marketplace })}
            </p>
            <p className="tabular text-[30px] font-semibold leading-none tracking-tight">
              {money(price)}
              <span className="ml-1.5 text-[13px] font-normal tracking-normal text-muted-foreground">
                {t('per ticket')}
                {typeof allIn === 'boolean' ? ` · ${allIn ? t('all-in') : t('+ fees')}` : ''}
              </span>
              {suspect && <span className="ml-1 text-[13px] font-normal tracking-normal text-muted-foreground">{t('· not confirmed')}</span>}
            </p>
          </>
        )}

        {next && <p className="mt-3 text-[13px] leading-snug">{next}</p>}
      </div>

      <a
        href={continueHref}
        data-continue=""
        rel="sponsored noopener"
        className="mt-4 flex items-center justify-center gap-1.5 rounded-lg bg-primary px-4 py-3 text-[15px] font-semibold text-primary-foreground transition-colors hover:bg-amber-deep"
      >
        {t('Continue to {marketplace}', { marketplace })}
        <ArrowUpRightIcon className="size-4" />
      </a>

      <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
        {t('You pay {marketplace} directly; TixInfo never sees your card.', { marketplace })}{' '}
        {t('{marketplace} shows the final total before you pay.', { marketplace })}
        {auto && (
          <span className={autoOnly.trim() || undefined}>
            <br />
            {t('Not redirected?')}{' '}
            <a href={continueHref} data-continue="" rel="sponsored noopener" className="underline underline-offset-2 hover:text-foreground">
              {t('Continue manually.')}
            </a>
          </span>
        )}
      </p>
    </div>
  );
};

export default RedirectCard;
